export const phishing_info = 'Phishing occurs when a cybercriminal attempts to get you to open up a malicious link or attachment with the intent of harvesting sensitive information.'

export const phishing_md = `
# Phishing
*Last updated on July 24, 2025*

![Types of Phishing image. Credit: sketchplanations @ https://sketchplanations.com/types-of-phishing](/images/sketchplanations-types-of-phishing.png "Types of Phishing image. Credit: sketchplanations @ https://sketchplanations.com/types-of-phishing")

## Overview

You login credentials, bank information, and identity are prime targets for cybercriminals. 
Phishing occurs when a cybercriminal attempts to get you to open up a malicious link or attachment with the intent of harvesting sensitive information.
These fraudulent items are typically hidden within a convincing-looking email, text message, browser notification, or social media message designed to incite urgency or to look like it came from a trusted source.

Please continue reading if you are interested in learning how to identify phishing attempts and protect yourself from this increasingly prevalent cyberthreat.

## 3 Simple Steps

The [Cybersecurity & Infrastructure Security Agency (CISA)](https://www.cisa.gov/) recommends following these [3 simple tips](https://www.cisa.gov/secure-our-world/recognize-and-report-phishing): 
1. ### Recognize Common Signs:
    - Requests for sensitive information
    - Unexpected attachments
    - Out of the ordinary communication
    - Shortened URLs
    - Suspicious email addresses or links
    - Improper spelling or grammar
    - Urgent or panic-inducing language
    - Too good to be true offers
    > **Trust your instincts.** If you suspect phishing, it likely is.
2. ### Resist
    - Resist the temptation to click on links or attachments. Verify the legitimacy of the message by research the sender before opening any attachments.
3. ### Delete
    - Delete the email from your inbox. Do not interact with content within the email — this includes any potential "Unsubscribe" links.

## A Deep Dive On Common Phishing Indicators

### Requesting Sensitive Information
- The main purpose of phishing attempts are to harvest your sensitive information. Whenever someone is asking you to provide this information, you should immediately become skeptical — it's your identity and money that you're giving someone access to after all.
- Clicking the link in one of these types of messages will usually lead you to a potentially trustworthy looking website with some form asking you for login credentials, SSN
- Here are some common phrases to look out for:
    - Please click the link below to verify your identity.
    - Please provide the following details: Full Name, SSN, Bank Account & Routing Number...
    - We've received a request to reset your password. If this wasn't you, please reset your password.
    - As part of routine security upgrades, all employees must re-authenticate their credentials using the secure link below.
- If you think an email is suspicious, don't access the website by clicking a link in the email. Google the website instead and access via your web browser.
### Unexpected Attachments
- Take a moment to stop ask yourself questions about any attachments. 
    - Does PayPal typically email me PDF attachments for my purchases?
    - Why is the IT department sending me a form that the HR department should have sent me?
    - Why does the icon for this PDF attachment look different?
    - Has Amazon ever included a PDF in their email communications to me?
- Posing these questions may help you jolt yourself out of a panicked, reactionary mindset and into a strategic and critical one instead.
- Some attackers may attempt to hide malware installers behind a pseudo-file extension such as credit_statement.pdf.exe. Notice how this file appears to be a PDF file, but is actually an executable file (exe). Downloading and opening this file may run malicious code instead of opening a PDF.
- It is important to take a step back and determine if this sender *has* or *should* be sending you documents.
### Out of the Ordinary Communication
- Most phishing attempts cast a wide net in hopes of catching a few individuals. 
- This means that the content they include in their email isn't necessarily always tailored to one individual. The larger of a group that they send their email to, the more likely it is that they find someone who actively uses the service that they're impersonating.
- With that in mind, you must ask yourself whether you've ever received communication from this service or if it's even relevant to you.
> This email from Norton says my payment information is expiring... I don't usually get emails from Norton... wait... I've never used Norton in my life!
### Shortened URLs
- Online services allow for the shortening of lengthy URLs into smaller ones. As a consequence, these shorter URLs hide any immediate information on the original link.
- Can you see how this could be dangerous? Only click on shortened URLs from trusted sources.
- Here are some examples of shortened URLs:
    - https://bit.ly/3XaYb9F
    - https://tinyurl.com/2p9f3j4r
    - https://rebrand.ly/abc123
    - https://blink.to/xyz456
    - https://short.gy/123abc
### Suspicious Email Addresses or Links
- A **domain** is a human-friendly identifier for a website, mail service, or other internet resource. A couple of well-known domains include: google.com, amazon.com, tanktechnology.com, and bockwaterheaters.com.
- Whenever you receive an email, the domain of the sender should match the branding of the email.
- In addition, the domain of the link the email is attempting to get you to click should match the context it is presenting itself with.
- For example:
    - You get an email from Microsoft saying that your account has been compromised. However, you notice that the sender is *notifications@**cloud-service.com***. Microsoft usually sends you emails from the **microsoft.com** domain so you know this is a phishing attempt.
    - You get a text message from PayPal with a link saying a purchase needs approval. However, the link appears as such: ***peypal.com**/approve_purchase?num=123456*. You remember that the correct PayPal domain is **paypal.com** and you've prevented another phishing attempt.
- If you ever see a hyperlink or button that you're weary of, you should hover over it without clicking. This will likely cause a pop-up to appear that shows you the underlying link. Inspect this for discrepancies.
- If the communication encourages you to contact the email or number listed, go to the service's official website instead and look for an alternative way of contacting them.
> Instead of using numbers like computers, we use domains to identify trusted services on the internet — **please use them to your advantage!**
### Improper Spelling or Grammar
- Be on the lookout for missing punctuation, misspelled words, and odd-sounding sentences. Official communications from trusted sources are rigorously reviewed in order to uphold a commitment to proper and effective orthography.  
> With the rise of AI, spelling and grammar issues are becoming less prevalent in phishing emails. It is not safe to solely rely on these sorts of indicators to identify phishing attempts.
### Urgent or Panic-Inducing Language
- This type of message is designed to create fear, urgency, and pressure the you into clicking a malicious link or providing sensitive information before you have time to think about what you're doing.
- If you feel like you're being pressured into a hasty decision, take a step back and try to find some other indicators that this may be a fishing attempt.
- Common phrases include:
    - Your account has been suspended.
    - Immediate action required.
    - Your payment failed — update now.
    - Unauthorized login attempt detected.
    - Confirm your identity to avoid service interruption.
    - Your account will be permanently deleted.
    - You have 24 hours to respond.
    - Final notice!
    - Your account has been compromised.
    - Click here to secure your account.
    - Act now to avoid penalties.
    - You are in violation of our terms of service.
    - Reset your password immediately.
    - Critical alert from [Bank/Company Name].
- **Never act quickly without thinking when reading an email.**
### Too Good to be True Offers
- Be critical of phishing attempts that try to lure you in with offers and claims that are incredibly enticing. This method exploits curiosity, greed, excitement, and urgency.
- Some examples include:
    - Winning a free gift card.
    - Cryptocurrency trade offers.
    - Work from home job offers that pay many thousands of dollars a week.
    - Absurdly cheap technology or flights.
    - Government tax refunds.
    - Urgent refunds or account credits.
- It's important to ask yourself whether you even signed up for these sorts of giveaways or newsletters.
> "There ain't no such thing as a free lunch."

## Resist Clicking
- A common misconception is that clicking on and opening a phishing email itself is dangerous — this is false. Your email app protects you from being attacked in this way. 
- After opening up the email, it is up to you to protect yourself from what is inside of it.
- In the event of a phishing attempt, report the email and block the sender. 
- Reporting the email sends it to your email provider, so they can update their security filters to protect others from similar attempts.


## References:
- [Recognize and Report Phishing by CSIA](https://www.cisa.gov/secure-our-world/recognize-and-report-phishing)
- [Phishing Information Guide by CSIA](https://www.cisa.gov/sites/default/files/2024-09/Secure-Our-World-Phishing-Tip-Sheet.pdf)

`
